import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Clock3, Plus, Shield, Star, UserRound, X } from 'lucide-react';
import { useI18n } from './I18nContext';
import { useData } from './DataContext';
import type { Player } from './types';
import { playerMinutes, playerOverall, playerStats, sortPlayers } from './playerUtils';
import { PlayerSheet } from './PlayerSheet';
import { staffForPlayer } from './staffAccess';

export function Players({
  requestedPlayerId,
  onRequestedPlayerHandled,
}: {
  requestedPlayerId?: string | null;
  onRequestedPlayerHandled?: () => void;
}) {
  const { t } = useI18n();
  const { players, matches, loading, error, createPlayer } = useData();
  const [selected, setSelected] = useState<Player | null>(null);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [savingPlayer, setSavingPlayer] = useState(false);
  const [playerMessage, setPlayerMessage] = useState('');
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    squadNumber: '',
    position: 'ST',
    photoUrl: '',
  });

  const sorted = useMemo(() => sortPlayers(players, matches), [players, matches]);

  useEffect(() => {
    if (!requestedPlayerId || !players.length) return;
    const target = players.find((player) => player.id === requestedPlayerId);
    if (target) setSelected(target);
    onRequestedPlayerHandled?.();
  }, [requestedPlayerId, players, onRequestedPlayerHandled]);

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="players-page">
      <section className="metal-card roster-heading">
        <div>
          <p className="eyebrow">PLAYER INTELLIGENCE</p>
          <h1>{t.roster}</h1>
          <p className="muted">
            {t.positionOrder} · {t.seasonMinutes}
          </p>
        </div>
        <div className="roster-heading-actions">
          <div className="roster-count">
            <strong>{players.length}</strong>
            <span>{t.registeredPlayers}</span>
          </div>
          <button className="add-player-button" type="button" onClick={() => {
            setPlayerMessage('');
            setShowAddPlayer(true);
          }}>
            <Plus size={18} />
            {t.addPlayer}
          </button>
        </div>
      </section>

      <section className="roster-grid">
        {sorted.map((player) => {
          const minutes = playerMinutes(matches, player.id);
          const stats = playerStats(matches, player.id);
          const overall = playerOverall(player);
          return (
            <button
              key={player.id}
              className="roster-player-card metal-card"
              onClick={() => setSelected(player)}
            >
              <div className="player-portrait">
                {player.photo_url
                  ? <img src={player.photo_url} alt={player.name} />
                  : <UserRound size={46} />}
                <span className="position-chip">{player.position || '—'}</span>
              </div>
              <div className="roster-player-copy">
                <div className="player-name-line">
                  <div className="player-name-stack">
                    <h3>{player.name}</h3>
                    {staffForPlayer(player.name)?.role === 'head_coach' && (
                      <span className="roster-head-coach-badge">{t.headCoach} + {t.player}</span>
                    )}
                  </div>
                  <strong className="overall-number">{overall}</strong>
                </div>
                <div className="player-meta-row">
                  <span>#{player.squad_number ?? '—'}</span>
                  <span><Clock3 size={14} /> {minutes}</span>
                  <span><Star size={14} /> {Number(player.current_form ?? 0).toFixed(1)}</span>
                </div>
                <div className="mini-status-bars">
                  <label>
                    <span>{t.fitness}</span>
                    <i><b style={{ width: `${Number(player.fitness ?? 0)}%` }} /></i>
                  </label>
                  <label>
                    <span>{t.morale}</span>
                    <i><b style={{ width: `${Number(player.morale ?? 0)}%` }} /></i>
                  </label>
                </div>
                <div className="player-stat-strip">
                  <span>{stats.apps} {t.apps}</span>
                  <span>{stats.goals} {t.goals}</span>
                  <span>{stats.assists} {t.assists}</span>
                </div>
              </div>
              <ChevronRight className="player-open-icon" size={20} />
            </button>
          );
        })}
      </section>

      {!players.length && (
        <section className="metal-card empty-panel">
          <Shield size={38} />
          <p>{t.noData}</p>
        </section>
      )}


      {showAddPlayer && (
        <div className="add-player-backdrop" role="presentation" onMouseDown={() => setShowAddPlayer(false)}>
          <section
            className="metal-card add-player-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-player-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p className="eyebrow">SQUAD MANAGEMENT</p>
                <h2 id="add-player-title">{t.addPlayer}</h2>
              </div>
              <button type="button" className="icon-steel-button" onClick={() => setShowAddPlayer(false)} aria-label={t.close}>
                <X size={18} />
              </button>
            </header>

            <div className="add-player-form">
              <label>
                <span>{t.playerName}</span>
                <input
                  autoFocus
                  value={newPlayer.name}
                  onChange={(event) => setNewPlayer({ ...newPlayer, name: event.target.value })}
                  placeholder={t.playerName}
                />
              </label>

              <div className="add-player-form-row">
                <label>
                  <span>{t.squadNumber}</span>
                  <input
                    type="number"
                    min="0"
                    max="99"
                    value={newPlayer.squadNumber}
                    onChange={(event) => setNewPlayer({ ...newPlayer, squadNumber: event.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '') })}
                    placeholder="81"
                  />
                </label>
                <label>
                  <span>{t.position}</span>
                  <select value={newPlayer.position} onChange={(event) => setNewPlayer({ ...newPlayer, position: event.target.value })}>
                    {['GK','RB','RCB','CB','LCB','LB','RWB','LWB','CDM','CM','CAM','RM','LM','RW','LW','CF','ST'].map((position) => (
                      <option key={position}>{position}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label>
                <span>{t.photoUrlOptional}</span>
                <input
                  value={newPlayer.photoUrl}
                  onChange={(event) => setNewPlayer({ ...newPlayer, photoUrl: event.target.value })}
                  placeholder="https://…"
                />
              </label>

              {playerMessage && <p className="form-message">{playerMessage}</p>}

              <button
                type="button"
                className="gold-button add-player-save"
                disabled={savingPlayer || !newPlayer.name.trim()}
                onClick={async () => {
                  setSavingPlayer(true);
                  setPlayerMessage('');
                  const result = await createPlayer({
                    name: newPlayer.name,
                    squad_number: newPlayer.squadNumber === '' ? null : Number(newPlayer.squadNumber),
                    position: newPlayer.position,
                    photo_url: newPlayer.photoUrl,
                  });
                  setSavingPlayer(false);
                  if (!result.ok) {
                    setPlayerMessage(result.message);
                    return;
                  }
                  setNewPlayer({ name: '', squadNumber: '', position: 'ST', photoUrl: '' });
                  setShowAddPlayer(false);
                }}
              >
                <Plus size={17} />
                {savingPlayer ? `${t.saving}…` : t.createPlayer}
              </button>
            </div>
          </section>
        </div>
      )}

      {selected && (
        <PlayerSheet
          player={selected}
          matches={matches}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
